import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import Reveal from "./Reveal";
import { WaIcon } from "./Nav";
import { SERVICES, waLink, WHATSAPP_LINK } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  phone: "",
  service: SERVICES[0].name,
  vehicle: "",
  address: "",
  preferred_date: "",
  notes: "",
};

const BookingSection = () => {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const waConfirm = waLink(
    `Hola fullshine! Soy ${form.name}. Quiero reservar: ${form.service}. Vehículo: ${form.vehicle || "—"}. Fecha preferida: ${form.preferred_date || "—"}. Dirección: ${form.address}.`
  );

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSent(true);
      toast.success("Solicitud enviada. Te confirmamos por WhatsApp.");
    } catch (err) {
      toast.error("No se pudo enviar la solicitud. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm(empty);
    setSent(false);
  };

  return (
    <section id="reservar" className="relative py-24 md:py-32 border-t border-white/10">
      <div
        className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 h-[500px] w-[700px] rounded-full bg-aqua/5 blur-[140px]"
        aria-hidden="true"
      />
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.25em] text-aqua uppercase">— Reservas</p>
          <h2 className="mt-4 font-display font-bold uppercase leading-[1.02] text-[clamp(2rem,4.5vw,3.6rem)]">
            Reserva tu <span className="text-outline">cita</span>
          </h2>
          <p className="mt-5 max-w-md text-white/60 leading-relaxed">
            Rellena el formulario y te confirmamos por WhatsApp. Si lo prefieres, pídela directamente
            con un toque.
          </p>

          <div className="mt-8 flex flex-col items-start gap-4">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-whatsapp-cta"
              className="inline-flex items-center gap-2.5 rounded-full bg-wa px-7 py-4 text-base font-semibold text-[#06120b] transition-all duration-300 hover:brightness-110 hover:shadow-[0_0_36px_rgba(37,211,102,0.45)]"
            >
              <WaIcon size={18} />
              Pedir cita por WhatsApp
            </a>
            <p className="font-mono text-[11px] tracking-[0.2em] text-white/40 uppercase">
              Sin pagos online — pagas cuando lo veas brillar
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          {sent ? (
            <div
              data-testid="booking-success-panel"
              className="glass rounded-3xl p-8 md:p-10 text-center"
            >
              <CheckCircle2 size={52} className="mx-auto text-aqua" />
              <h3 className="mt-5 font-display text-2xl font-bold">Solicitud recibida</h3>
              <p className="mt-3 text-white/60">
                Guardamos tu reserva. Confírmala ahora por WhatsApp y te respondemos al momento.
              </p>
              <a
                href={waConfirm}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-whatsapp-confirm"
                className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-wa px-7 py-3.5 text-base font-semibold text-[#06120b] transition-all duration-300 hover:brightness-110"
              >
                <WaIcon size={18} />
                Confirmar por WhatsApp
              </a>
              <button
                onClick={reset}
                data-testid="booking-reset-button"
                className="mt-5 block mx-auto text-sm text-white/50 underline-offset-4 hover:text-aqua hover:underline"
              >
                Hacer otra reserva
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="glass rounded-3xl p-6 md:p-9 grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <label className="sm:col-span-1 block">
                <span className="mb-1.5 block text-xs font-mono tracking-wider text-white/50 uppercase">Nombre *</span>
                <input
                  className="field"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Tu nombre"
                  data-testid="booking-field-name"
                />
              </label>
              <label className="sm:col-span-1 block">
                <span className="mb-1.5 block text-xs font-mono tracking-wider text-white/50 uppercase">Teléfono *</span>
                <input
                  className="field"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="600 000 000"
                  data-testid="booking-field-phone"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-mono tracking-wider text-white/50 uppercase">Servicio *</span>
                <select className="field" value={form.service} onChange={set("service")} data-testid="booking-field-service">
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.name}>
                      {s.name} — {s.price}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-mono tracking-wider text-white/50 uppercase">Fecha preferida</span>
                <input
                  className="field"
                  type="date"
                  value={form.preferred_date}
                  onChange={set("preferred_date")}
                  data-testid="booking-field-date"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-mono tracking-wider text-white/50 uppercase">Vehículo</span>
                <input
                  className="field"
                  value={form.vehicle}
                  onChange={set("vehicle")}
                  placeholder="Marca y modelo"
                  data-testid="booking-field-vehicle"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-mono tracking-wider text-white/50 uppercase">Dirección *</span>
                <input
                  className="field"
                  required
                  value={form.address}
                  onChange={set("address")}
                  placeholder="Calle, número, ciudad"
                  data-testid="booking-field-address"
                />
              </label>
              <label className="sm:col-span-2 block">
                <span className="mb-1.5 block text-xs font-mono tracking-wider text-white/50 uppercase">Notas</span>
                <textarea
                  className="field min-h-[90px] resize-y"
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder="Cuéntanos algo del coche, manchas, horarios..."
                  data-testid="booking-field-notes"
                />
              </label>
              <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit"
                className="sm:col-span-2 rounded-xl bg-aqua py-4 font-semibold text-ink transition-all duration-300 hover:shadow-[0_0_36px_rgba(0,242,254,0.35)] disabled:opacity-50 disabled:cursor-wait"
              >
                {sending ? "Enviando..." : "Enviar solicitud de reserva"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default BookingSection;
